import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    CpmBadge,
    CpmButton,
    CpmErrorCard,
    CpmModal,
    CpmPageShell,
    CpmSkeletonRow,
    CpmTooltip,
} from '../../components/design-system';
import { useAppDispatch } from '../../store';
import {
    fetchConfigTemplateStates,
    useConfigTemplateStates,
} from '../../store/slices/configTemplatesListSlice';
import { updateConfigTemplateStatus } from '../../services/configTemplateService';
import type { ConfigTemplateLevel } from '../../types/configTemplate.types';
import {
    CONFIG_TEMPLATE_COPY,
    applyActionLabel,
    applyTipBody,
    applyTipTitle,
    backToListLabel,
    copiedFromLine,
    copyDidNotComeBody,
    copyPickerTipBody,
    editorPath,
    identitySummaryLine,
    listPath,
    nameClashBody,
    rowStatusFailedLabel,
    searchJumpLine,
    settingsBadgeLabel,
    settingsInCount,
} from './configTemplateCopy';
import ApplyFlow from './components/ApplyFlow';
import CopyFromLiveFlow from './components/CopyFromLiveFlow';
import IdentityFields from './components/IdentityFields';
import SegmentedToggle from './components/SegmentedToggle';
import SettingsPane from './components/SettingsPane';
import SettingsRail from './components/SettingsRail';
import { useTemplateEditorDraft } from './hooks/useTemplateEditorDraft';
import {
    bestMatchingPartId,
    changedFieldPaths,
    clustersWithChangedFields,
    countGroupMatches,
    countPartMatches,
} from './utils/settingsDiff';

function levelFromParam(place?: string): ConfigTemplateLevel {
    return place === 'store-level' ? 'STORE' : 'STORE_BRAND';
}

function placeLabel(level: ConfigTemplateLevel): string {
    return level === 'STORE_BRAND'
        ? CONFIG_TEMPLATE_COPY.PLACE_BRAND.toLowerCase()
        : CONFIG_TEMPLATE_COPY.PLACE_STORE.toLowerCase();
}

function copyFromLiveLabel(level: ConfigTemplateLevel): string {
    return level === 'STORE_BRAND' ? 'Copy from a live store brand' : 'Copy from a live store';
}

export default function TemplateEditorPage() {
    const { place, id } = useParams<{ place?: string; id?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const level = levelFromParam(place);
    const isNew = !id || id === 'new';
    const duplicateFrom = searchParams.get('duplicateFrom');
    const editor = useTemplateEditorDraft({
        level,
        id: isNew ? null : id,
        duplicateFrom: isNew ? duplicateFrom : null,
    });
    const states = useConfigTemplateStates();
    const [step, setStep] = useState<'identity' | 'settings'>(isNew ? 'identity' : 'settings');
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [view, setView] = useState<'all' | 'in'>('all');
    const [settingsQuery, setSettingsQuery] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [searchWaiting, setSearchWaiting] = useState(false);
    const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
    const [jumpPartId, setJumpPartId] = useState<string | null>(null);
    const [showChanged, setShowChanged] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [applyOpen, setApplyOpen] = useState(false);
    const [copiedFlash, setCopiedFlash] = useState<string | null>(null);
    const [toggleBusy, setToggleBusy] = useState(false);
    const [toggleFailed, setToggleFailed] = useState(false);
    const [identityErrors, setIdentityErrors] = useState(false);

    useEffect(() => {
        if (states.status === 'idle') void dispatch(fetchConfigTemplateStates());
    }, [dispatch, states.status]);

    useEffect(() => {
        if (!isNew) setStep('settings');
    }, [isNew]);

    const parts = useMemo(
        () => editor.schema?.groups.flatMap((group) => group.parts) ?? [],
        [editor.schema]
    );

    useEffect(() => {
        if (!selectedPartId && parts[0]) setSelectedPartId(parts[0].id);
    }, [parts, selectedPartId]);

    const changedPaths = useMemo(
        () => (editor.draft ? changedFieldPaths(editor.draft) : new Set<string>()),
        [editor.draft]
    );
    const changedCount = changedPaths.size;

    useEffect(() => {
        const trimmed = settingsQuery.trim();
        if (trimmed.length < 2) {
            setSearchWaiting(false);
            setAppliedQuery('');
            return;
        }
        setSearchWaiting(true);
        const timer = window.setTimeout(() => {
            setSearchWaiting(false);
            setAppliedQuery(trimmed.toLowerCase());
        }, 260);
        return () => window.clearTimeout(timer);
    }, [settingsQuery]);

    useEffect(() => {
        if (!appliedQuery || !editor.schema) return;
        const best = bestMatchingPartId(editor.schema, appliedQuery);
        if (!best) return;
        setShowChanged(false);
        setSelectedPartId(best);
        setJumpPartId(best);
    }, [appliedQuery, editor.schema]);

    useEffect(() => {
        if (showChanged && changedCount === 0) setShowChanged(false);
    }, [showChanged, changedCount]);

    const matchCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        if (!appliedQuery || !editor.schema) return counts;
        for (const group of editor.schema.groups) {
            for (const part of group.parts) {
                counts[part.id] = countPartMatches(part, appliedQuery);
            }
        }
        return counts;
    }, [appliedQuery, editor.schema]);

    const groupMatchCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        if (!appliedQuery || !editor.schema) return counts;
        for (const group of editor.schema.groups) {
            counts[group.id] = countGroupMatches(group, appliedQuery);
        }
        return counts;
    }, [appliedQuery, editor.schema]);

    const totalMatches = useMemo(
        () => Object.values(matchCounts).reduce((sum, n) => sum + n, 0),
        [matchCounts]
    );

    const selectedPart = showChanged
        ? {
              id: '__changed__',
              title: CONFIG_TEMPLATE_COPY.CHANGED_SINCE_COPY,
              clusters: editor.schema
                  ? clustersWithChangedFields(editor.schema, changedPaths)
                  : [],
          }
        : (parts.find((part) => part.id === selectedPartId) ?? null);
    const selectedGroupTitle = showChanged
        ? editor.draft?.copySourceName
            ? copiedFromLine(editor.draft.copySourceName)
            : 'Since the copy'
        : (editor.schema?.groups.find((group) =>
              group.parts.some((part) => part.id === selectedPartId)
          )?.title ?? '');
    const totalLeaves = editor.fields.length;
    const inCount = editor.draft?.included.length ?? 0;
    const applyReason = !editor.draft
        ? 'loading'
        : editor.saving ||
            editor.schemaStatus !== 'success' ||
            editor.bootStatus === 'loading'
          ? 'loading'
          : !editor.draft.id
            ? 'unsaved'
            : editor.dirty
              ? 'dirty'
              : editor.draft.identity.activeStatus !== 'Active'
                ? 'inactive'
                : inCount === 0
                  ? 'empty'
                  : null;
    const canCopy = Boolean(editor.draft?.identity.name.trim());
    const canApply = applyReason === null;
    const copyTitle = canCopy ? undefined : CONFIG_TEMPLATE_COPY.COPY_NAME_REQUIRED;
    const identityStep = step === 'identity' && isNew;
    const isExistingSaved = !isNew && Boolean(editor.draft?.id);
    const nameErrorText =
        editor.saveError === 'name'
            ? CONFIG_TEMPLATE_COPY.NAME_REQUIRED_SAVE
            : editor.saveError === 'clash' && editor.draft
              ? nameClashBody(level, editor.draft.identity.name.trim())
              : undefined;
    const onIdentityChange = (patch: Parameters<typeof editor.updateIdentity>[0]) => {
        editor.updateIdentity(patch);
        if (patch.name !== undefined) editor.clearSaveError();
    };
    const fieldsFailed = Boolean(editor.copyFail) || editor.schemaStatus === 'error';

    const requestLeave = () => {
        if (editor.dirty) {
            setLeaveOpen(true);
            return;
        }
        navigate(listPath(level));
    };

    const toggleActive = async () => {
        if (!editor.draft?.id) return;
        setToggleBusy(true);
        setToggleFailed(false);
        try {
            const next = editor.draft.identity.activeStatus === 'Active' ? 'Inactive' : 'Active';
            await updateConfigTemplateStatus({ id: editor.draft.id, activeStatus: next });
            editor.updateIdentity({ activeStatus: next });
        } catch {
            setToggleFailed(true);
        } finally {
            setToggleBusy(false);
        }
    };

    const clearSearch = () => {
        setSettingsQuery('');
        setAppliedQuery('');
        setSearchWaiting(false);
    };

    if (editor.bootStatus === 'loading') {
        return (
            <CpmPageShell gap={22} maxWidth={1420} variant="masterEditor">
                <CpmSkeletonRow index={0} />
                <CpmSkeletonRow index={1} />
                <CpmSkeletonRow index={2} />
            </CpmPageShell>
        );
    }

    if (editor.bootStatus === 'error' || !editor.draft) {
        return (
            <CpmPageShell gap={22} maxWidth={1080} variant="compactBottom">
                <CpmErrorCard
                    title={CONFIG_TEMPLATE_COPY.IDENTITY_FAIL_TITLE}
                    body={CONFIG_TEMPLATE_COPY.IDENTITY_FAIL_BODY}
                    onRetry={() => void editor.retryBoot()}
                    retryLabel={CONFIG_TEMPLATE_COPY.RETRY}
                />
            </CpmPageShell>
        );
    }

    const kicker = isNew
        ? duplicateFrom
            ? `Duplicate of a ${placeLabel(level)} template`
            : `New ${placeLabel(level)} template`
        : `${placeLabel(level)} template`;
    const stepLine = isNew
        ? step === 'identity'
            ? 'Step 1 of 2 · This template'
            : 'Step 2 of 2 · Settings'
        : null;

    return (
        <div
            className="cpm-theme flex flex-col h-screen overflow-hidden"
            style={{
                backgroundColor: 'var(--cpm-page-bg)',
            }}
        >
            <header
                className="flex items-center flex-wrap"
                style={{
                    flex: 'none',
                    gap: 12,
                    padding: '13px 26px',
                    background: '#fff',
                    borderBottom: '1px solid var(--cpm-border-default)',
                    boxShadow: '0 2px 12px -8px rgba(14,8,88,0.4)',
                }}
            >
                <CpmButton
                    variant="ghost"
                    size="sm"
                    onClick={requestLeave}
                    leadingIcon={<BackArrowIcon />}
                >
                    {backToListLabel(level)}
                </CpmButton>
                <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                    <div className="flex items-center" style={{ gap: 8 }}>
                        <span
                            className="uppercase"
                            style={{
                                fontSize: 10.5,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                color: 'var(--cpm-brand-purple)',
                            }}
                        >
                            {kicker}
                        </span>
                        <CpmTooltip
                            content="Brand-level settings and store-level settings are never in one template. To author the other level, go back and switch tab."
                            align="start"
                            fit
                        >
                            <span
                                className="inline-flex items-center"
                                style={{ color: 'var(--cpm-text-tertiary)' }}
                            >
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="11" x2="12" y2="16.5" strokeLinecap="round" />
                                    <circle
                                        cx="12"
                                        cy="7.8"
                                        r="0.9"
                                        fill="currentColor"
                                        stroke="none"
                                    />
                                </svg>
                            </span>
                        </CpmTooltip>
                        {stepLine ? (
                            <span
                                className="uppercase"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                {stepLine}
                            </span>
                        ) : null}
                    </div>
                    <div className="flex items-center min-w-0" style={{ gap: 9 }}>
                        <span
                            className="overflow-hidden text-ellipsis whitespace-nowrap"
                            style={{
                                fontSize: 19,
                                fontWeight: 800,
                                letterSpacing: '-0.022em',
                                color: 'var(--cpm-text-primary)',
                            }}
                        >
                            {editor.draft.identity.name.trim() || CONFIG_TEMPLATE_COPY.UNTITLED}
                        </span>
                        <CpmBadge
                            tone={
                                editor.draft.identity.activeStatus === 'Active'
                                    ? 'success'
                                    : 'neutral'
                            }
                            dot
                        >
                            {editor.draft.identity.activeStatus === 'Active'
                                ? CONFIG_TEMPLATE_COPY.ACTIVE
                                : CONFIG_TEMPLATE_COPY.INACTIVE}
                        </CpmBadge>
                        <span
                            className="tabular-nums"
                            style={{
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            {settingsInCount(inCount, totalLeaves)}
                        </span>
                    </div>
                </div>
                <div className="flex-1" />
                <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                    {!identityStep ? (
                        <>
                            {isNew ? (
                                <CpmButton
                                    variant="ghost"
                                    size="sm"
                                    disabled={editor.saving}
                                    onClick={() => setStep('identity')}
                                >
                                    Back to details
                                </CpmButton>
                            ) : null}
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={editor.saving}
                                onClick={() => setDetailsOpen(true)}
                            >
                                {CONFIG_TEMPLATE_COPY.EDIT_DETAILS}
                            </CpmButton>
                            <CpmTooltip
                                title={applyTipTitle(!canApply)}
                                content={applyTipBody(applyReason)}
                                align="end"
                                fit
                                maxWidth={268}
                            >
                                <span
                                    className="inline-flex items-center"
                                    style={{ color: 'var(--cpm-text-tertiary)' }}
                                >
                                    <AlertCircleIcon />
                                </span>
                            </CpmTooltip>
                            <CpmButton
                                variant="outline"
                                size="sm"
                                disabled={!canApply}
                                onClick={() => setApplyOpen(true)}
                                style={{
                                    color: canApply
                                        ? 'var(--cpm-text-primary)'
                                        : undefined,
                                }}
                            >
                                {applyActionLabel(level)}
                            </CpmButton>
                        </>
                    ) : null}
                    {isExistingSaved ? (
                        <>
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={editor.saving || toggleBusy}
                                leadingIcon={<DuplicateIcon />}
                                onClick={() =>
                                    navigate(
                                        `${editorPath(level)}?duplicateFrom=${editor.draft?.id}`
                                    )
                                }
                            >
                                {CONFIG_TEMPLATE_COPY.DUPLICATE}
                            </CpmButton>
                            {toggleFailed ? (
                                <div className="flex items-center" style={{ gap: 8 }}>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: 'var(--cpm-shortfall-text)',
                                        }}
                                    >
                                        {rowStatusFailedLabel(editor.draft.identity.activeStatus)}
                                    </span>
                                    <CpmButton
                                        variant="outline"
                                        size="sm"
                                        disabled={editor.saving || toggleBusy}
                                        isLoading={toggleBusy}
                                        onClick={() => void toggleActive()}
                                    >
                                        {CONFIG_TEMPLATE_COPY.RETRY_ROW}
                                    </CpmButton>
                                </div>
                            ) : (
                                <CpmButton
                                    variant="ghost"
                                    size="sm"
                                    disabled={editor.saving}
                                    isLoading={toggleBusy}
                                    onClick={() => void toggleActive()}
                                >
                                    {editor.draft.identity.activeStatus === 'Active'
                                        ? CONFIG_TEMPLATE_COPY.TURN_OFF
                                        : CONFIG_TEMPLATE_COPY.TURN_ON}
                                </CpmButton>
                            )}
                        </>
                    ) : null}
                </div>
            </header>

            <div
                className="flex-1 min-h-0 overflow-hidden flex flex-col"
                style={{ padding: '20px 26px 26px' }}
            >
                {identityStep ? (
                    <div
                        className="mx-auto w-full max-w-[760px] rounded-[16px] border border-[var(--cpm-border-default)] bg-white p-6 flex flex-col"
                        style={{ gap: 20, boxShadow: 'var(--cpm-shadow-card)' }}
                    >
                        {editor.saveError === 'request' ? (
                            <SaveFailBanner onRetry={() => void editor.save()} />
                        ) : null}
                        <IdentityFields
                            level={level}
                            identity={editor.draft.identity}
                            states={states.states}
                            statesStatus={states.status}
                            onRetryStates={() => void dispatch(fetchConfigTemplateStates())}
                            onChange={onIdentityChange}
                            showErrors={identityErrors}
                            nameErrorText={nameErrorText}
                        />
                        <div
                            className="flex items-center"
                            style={{
                                gap: 10,
                                paddingTop: 16,
                                borderTop: '1px solid var(--cpm-border-default)',
                            }}
                        >
                            <CopyFromLiveButton
                                label={copyFromLiveLabel(level)}
                                disabled={!canCopy}
                                title={copyTitle}
                                onClick={() => setCopyOpen(true)}
                            />
                            {!canCopy ? (
                                <span
                                    className="flex-none"
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        color: 'var(--cpm-open-text)',
                                    }}
                                >
                                    {CONFIG_TEMPLATE_COPY.COPY_NAME_FIRST}
                                </span>
                            ) : null}
                            <span
                                className="ml-auto text-right"
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-secondary)',
                                    maxWidth: 360,
                                    lineHeight: 1.45,
                                }}
                            >
                                {CONFIG_TEMPLATE_COPY.IDENTITY_SAVE_NOTE}
                            </span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            className="flex flex-wrap items-center flex-none"
                            style={{ gap: 9, marginBottom: 12 }}
                        >
                            <div
                                className="flex items-center justify-center flex-none"
                                style={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: 'var(--cpm-radius-8)',
                                    background: 'var(--cpm-purple-tint-13)',
                                    color: 'var(--cpm-brand-purple)',
                                }}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <line x1="4" y1="7" x2="20" y2="7" strokeLinecap="round" />
                                    <line x1="4" y1="12" x2="20" y2="12" strokeLinecap="round" />
                                    <line x1="4" y1="17" x2="20" y2="17" strokeLinecap="round" />
                                </svg>
                            </div>
                            <span
                                style={{
                                    fontSize: 14.5,
                                    fontWeight: 800,
                                    color: 'var(--cpm-text-primary)',
                                }}
                            >
                                Settings in this template
                            </span>
                            <CpmBadge tone={inCount === 0 ? 'neutral' : 'brand'}>
                                {settingsBadgeLabel(inCount)}
                            </CpmBadge>
                            <CpmTooltip
                                title={CONFIG_TEMPLATE_COPY.IN_THIS_TEMPLATE}
                                content="Only settings you leave in are written when you apply. Off, 0, and empty still write. A setting you take out is left as it already is on the store brand or store."
                                align="start"
                                fit
                                maxWidth={268}
                            >
                                <span
                                    className="inline-flex items-center"
                                    style={{ color: 'var(--cpm-text-tertiary)' }}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line
                                            x1="12"
                                            y1="11"
                                            x2="12"
                                            y2="16.5"
                                            strokeLinecap="round"
                                        />
                                        <circle
                                            cx="12"
                                            cy="7.8"
                                            r="0.9"
                                            fill="currentColor"
                                            stroke="none"
                                        />
                                    </svg>
                                </span>
                            </CpmTooltip>
                            <div className="flex-1" />
                            <CopyFromLiveButton
                                label={copyFromLiveLabel(level)}
                                disabled={!canCopy}
                                title={copyTitle}
                                tip={copyPickerTipBody(level)}
                                onClick={() => setCopyOpen(true)}
                            />
                        </div>

                        <div
                            className="flex flex-wrap items-center flex-none"
                            style={{ gap: 10, marginBottom: 12 }}
                        >
                            <div className="relative flex-none" style={{ width: 430 }}>
                                <span
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                                    style={{ color: 'var(--cpm-text-tertiary)' }}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.2"
                                    >
                                        <circle cx="11" cy="11" r="7" />
                                        <path d="m20 20-3.2-3.2" strokeLinecap="round" />
                                    </svg>
                                </span>
                                <input
                                    value={settingsQuery}
                                    onChange={(event) => setSettingsQuery(event.target.value)}
                                    placeholder={CONFIG_TEMPLATE_COPY.SETTINGS_SEARCH}
                                    className="w-full h-[38px] rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white pl-9 pr-3 text-[13px] outline-none focus:border-[var(--cpm-brand-purple)]"
                                />
                            </div>
                            {searchWaiting ? (
                                <div className="flex items-center" style={{ gap: 8 }}>
                                    <span className="cpm-pulse-dot" />
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        {CONFIG_TEMPLATE_COPY.FINDING_SETTINGS}
                                    </span>
                                </div>
                            ) : null}
                            {appliedQuery ? (
                                <>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: 'var(--cpm-brand-navy)',
                                        }}
                                    >
                                        {searchJumpLine({
                                            query: settingsQuery.trim(),
                                            matches: totalMatches,
                                            groupTitle: selectedGroupTitle,
                                            partTitle: selectedPart?.title ?? '',
                                        })}
                                    </span>
                                    <CpmButton variant="ghost" size="sm" onClick={clearSearch}>
                                        {CONFIG_TEMPLATE_COPY.CLEAR_SEARCH}
                                    </CpmButton>
                                </>
                            ) : null}
                            <div className="flex-1" />
                            <SegmentedToggle
                                size="sm"
                                equal={false}
                                value={view}
                                options={[
                                    { value: 'all', label: CONFIG_TEMPLATE_COPY.EVERY_SETTING },
                                    { value: 'in', label: CONFIG_TEMPLATE_COPY.IN_THIS_TEMPLATE },
                                ]}
                                onChange={setView}
                            />
                            {editor.draft.copySourceName ? (
                                <span
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        color: 'var(--cpm-brand-purple)',
                                    }}
                                >
                                    {copiedFlash ?? copiedFromLine(editor.draft.copySourceName)}
                                </span>
                            ) : copiedFlash ? (
                                <span
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        color: 'var(--cpm-brand-purple)',
                                    }}
                                >
                                    {copiedFlash}
                                </span>
                            ) : null}
                        </div>

                        {editor.saveError === 'request' ? (
                            <div style={{ marginBottom: 16 }}>
                                <SaveFailBanner onRetry={() => void editor.save()} />
                            </div>
                        ) : null}

                        {fieldsFailed ? (
                            <div className="flex flex-col items-start" style={{ gap: 12, padding: '8px 0 16px' }}>
                                <div
                                    style={{
                                        fontSize: 14.5,
                                        fontWeight: 800,
                                        color: 'var(--cpm-text-primary)',
                                    }}
                                >
                                    {editor.copyFail
                                        ? CONFIG_TEMPLATE_COPY.COPY_DID_NOT_COME
                                        : CONFIG_TEMPLATE_COPY.FIELDS_FAIL_TITLE}
                                </div>
                                <div
                                    style={{
                                        fontSize: 12.5,
                                        fontWeight: 500,
                                        lineHeight: 1.55,
                                        color: 'var(--cpm-text-secondary)',
                                        maxWidth: 560,
                                        textWrap: 'pretty',
                                    }}
                                >
                                    {editor.copyFail
                                        ? copyDidNotComeBody(level, editor.copyFail.name)
                                        : CONFIG_TEMPLATE_COPY.FIELDS_FAIL_BODY}
                                </div>
                                <CpmButton
                                    variant="navy"
                                    size="sm"
                                    isLoading={editor.copyRetrying || editor.schemaStatus === 'loading'}
                                    onClick={() =>
                                        editor.copyFail
                                            ? void editor.retryCopy()
                                            : void editor.retrySchema()
                                    }
                                >
                                    {editor.copyFail
                                        ? CONFIG_TEMPLATE_COPY.RETRY_THE_COPY
                                        : CONFIG_TEMPLATE_COPY.RETRY}
                                </CpmButton>
                            </div>
                        ) : editor.schemaStatus === 'loading' || !editor.schema ? (
                            <div
                                className="grid grid-cols-[320px_minmax(0,1fr)]"
                                style={{ gap: 16 }}
                            >
                                <CpmSkeletonRow index={0} />
                                <CpmSkeletonRow index={1} />
                            </div>
                        ) : (
                            <div
                                className="flex items-stretch min-h-0 flex-1"
                                style={{ gap: 16 }}
                            >
                                <SettingsRail
                                    schema={editor.schema}
                                    included={editor.draft.included}
                                    selectedPartId={selectedPartId}
                                    onSelectPart={(partId) => {
                                        setShowChanged(false);
                                        setSelectedPartId(partId);
                                        setJumpPartId(partId);
                                    }}
                                    jumpPartId={jumpPartId}
                                    matchCounts={matchCounts}
                                    groupMatchCounts={groupMatchCounts}
                                    searchOn={Boolean(appliedQuery)}
                                    changed={
                                        editor.draft.copyBaseline
                                            ? {
                                                  count: changedCount,
                                                  sourceName: editor.draft.copySourceName,
                                                  selected: showChanged,
                                              }
                                            : null
                                    }
                                    onSelectChanged={() => setShowChanged(true)}
                                />
                                <SettingsPane
                                    groupTitle={selectedGroupTitle}
                                    part={selectedPart}
                                    included={editor.draft.included}
                                    values={editor.draft.values}
                                    copyBaseline={editor.draft.copyBaseline}
                                    view={view}
                                    mode={showChanged ? 'changed' : 'part'}
                                    searchQuery={appliedQuery}
                                    copySourceName={editor.draft.copySourceName}
                                    onShowEverySetting={() => setView('all')}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div
                className="flex items-center justify-between flex-wrap"
                style={{
                    flex: 'none',
                    gap: 14,
                    padding: '14px 26px',
                    borderTop: '1px solid var(--cpm-border-default)',
                    background: 'rgba(249,249,252,0.92)',
                    backdropFilter: 'blur(14px)',
                    boxShadow: '0 -6px 18px -8px rgba(14,8,88,0.14)',
                }}
            >
                <span
                    style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}
                >
                    {identityStep
                        ? CONFIG_TEMPLATE_COPY.FOOTER_SAVE_NOTE
                        : CONFIG_TEMPLATE_COPY.FOOTER_APPLY_NOTE}
                </span>
                <div className="flex items-center" style={{ gap: 9 }}>
                    {editor.saving ? (
                        <>
                            <span
                                className="animate-pulse"
                                style={{
                                    display: 'block',
                                    width: 9,
                                    height: 9,
                                    borderRadius: 'var(--cpm-radius-full)',
                                    background: 'var(--cpm-brand-purple)',
                                }}
                            />
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                Saving…
                            </span>
                        </>
                    ) : null}
                    {editor.saveError === 'request' ? (
                        <CpmButton
                            variant="outline"
                            size="sm"
                            disabled={editor.saving}
                            onClick={() => void editor.save()}
                        >
                            {CONFIG_TEMPLATE_COPY.RETRY_SAVE}
                        </CpmButton>
                    ) : null}
                    {editor.savedFlash ? (
                        <span className="text-[13px] font-extrabold text-[var(--cpm-success-text-light)]">
                            {CONFIG_TEMPLATE_COPY.SAVED}
                        </span>
                    ) : null}
                    {identityStep ? (
                        <>
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                {identitySummaryLine(editor.draft.identity)}
                            </span>
                            <CpmButton
                                variant="navy"
                                size="md"
                                onClick={() => {
                                    const named = Boolean(editor.draft?.identity.name.trim());
                                    const covered =
                                        Boolean(editor.draft?.identity.allStates) ||
                                        (editor.draft?.identity.states.length ?? 0) > 0;
                                    if (!named || !covered) {
                                        setIdentityErrors(true);
                                        return;
                                    }
                                    setIdentityErrors(false);
                                    setStep('settings');
                                }}
                            >
                                {CONFIG_TEMPLATE_COPY.CONTINUE_TO_SETTINGS}
                            </CpmButton>
                        </>
                    ) : (
                        <CpmButton
                            variant="navy"
                            size="md"
                            disabled={editor.saving || !editor.draft.identity.name.trim()}
                            isLoading={editor.saving}
                            onClick={async () => {
                                const saved = await editor.save();
                                if (saved && isNew) {
                                    navigate(editorPath(level, saved._id), { replace: true });
                                }
                            }}
                        >
                            {CONFIG_TEMPLATE_COPY.SAVE}
                        </CpmButton>
                    )}
                </div>
            </div>

            <CpmModal open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth={760} zIndex={100}>
                <div
                    className="flex flex-col"
                    style={{
                        padding: '20px 22px 22px',
                        gap: 20,
                        maxHeight: 'calc(100vh - 112px)',
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                    }}
                >
                    {editor.saveError === 'request' ? (
                        <SaveFailBanner onRetry={() => void editor.save()} />
                    ) : null}
                    <IdentityFields
                        level={level}
                        identity={editor.draft.identity}
                        states={states.states}
                        statesStatus={states.status}
                        onRetryStates={() => void dispatch(fetchConfigTemplateStates())}
                        onChange={onIdentityChange}
                        nameErrorText={nameErrorText}
                        headingAction={
                            <CpmButton
                                variant="navy"
                                size="sm"
                                onClick={() => setDetailsOpen(false)}
                            >
                                {CONFIG_TEMPLATE_COPY.DONE}
                            </CpmButton>
                        }
                        headingNote={CONFIG_TEMPLATE_COPY.DONE_KEEPS_DRAFT}
                    />
                </div>
            </CpmModal>

            <CpmModal
                open={leaveOpen}
                onClose={() => setLeaveOpen(false)}
                maxWidth={500}
                closeOnEscape={!editor.saving}
                closeOnBackdrop={!editor.saving}
            >
                <div
                    className="flex flex-col"
                    style={{ padding: '22px 22px 18px', gap: 10 }}
                >
                    <div
                        style={{
                            fontSize: 17,
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            color: 'var(--cpm-text-primary)',
                        }}
                    >
                        {CONFIG_TEMPLATE_COPY.LEAVE_TITLE}
                    </div>
                    <div
                        style={{
                            fontSize: 12.5,
                            fontWeight: 500,
                            lineHeight: 1.55,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {CONFIG_TEMPLATE_COPY.LEAVE_BODY}
                    </div>
                    <div
                        className="flex flex-wrap items-center justify-end"
                        style={{ gap: 9, marginTop: 6 }}
                    >
                        <CpmButton
                            variant="ghost"
                            size="md"
                            disabled={editor.saving}
                            onClick={() => {
                                editor.discard();
                                navigate(listPath(level));
                            }}
                        >
                            {CONFIG_TEMPLATE_COPY.LEAVE_DISCARD}
                        </CpmButton>
                        <CpmButton
                            variant="navy"
                            size="md"
                            disabled={editor.saving}
                            onClick={() => setLeaveOpen(false)}
                        >
                            {CONFIG_TEMPLATE_COPY.LEAVE_STAY}
                        </CpmButton>
                    </div>
                </div>
            </CpmModal>

            <CopyFromLiveFlow
                open={copyOpen}
                onClose={() => setCopyOpen(false)}
                level={level}
                draft={editor.draft}
                fields={editor.fields}
                states={states.states}
                statesStatus={states.status}
                dirty={editor.dirty}
                onCopied={(message) => {
                    editor.clearCopyFail();
                    setCopiedFlash(message);
                    setStep('settings');
                    window.setTimeout(() => setCopiedFlash(null), 2400);
                }}
                onCopyFailed={(source) => {
                    editor.markCopyFailed(source);
                    setStep('settings');
                }}
            />
            {editor.draft.id ? (
                <ApplyFlow
                    open={applyOpen}
                    onClose={() => setApplyOpen(false)}
                    level={level}
                    draft={editor.draft}
                    schema={editor.schema}
                    states={states.states}
                    statesStatus={states.status}
                />
            ) : null}
        </div>
    );
}

function SaveFailBanner({ onRetry }: { onRetry: () => void }) {
    return (
        <div
            style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: 'var(--cpm-shortfall-bg)',
                border: '1px solid var(--cpm-shortfall-border)',
            }}
        >
            <p
                className="m-0"
                style={{ fontSize: 13, fontWeight: 800, color: 'var(--cpm-shortfall-text)' }}
            >
                {CONFIG_TEMPLATE_COPY.SAVE_FAIL_TITLE}
            </p>
            <p
                className="m-0"
                style={{
                    marginTop: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.45,
                    color: 'var(--cpm-text-secondary)',
                }}
            >
                {CONFIG_TEMPLATE_COPY.SAVE_FAIL_BODY}
            </p>
            <div style={{ marginTop: 10 }}>
                <CpmButton variant="outline" size="sm" onClick={onRetry}>
                    {CONFIG_TEMPLATE_COPY.RETRY_SAVE}
                </CpmButton>
            </div>
        </div>
    );
}

function BackArrowIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
        >
            <path d="M15 6 9 12l6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function DuplicateIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );
}

function MergeIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M8 3v4a4 4 0 0 0 4 4h8" />
            <path d="M8 21v-4a4 4 0 0 1 4-4h8" />
            <path d="m17 8 4 3-4 3" />
        </svg>
    );
}

function AlertCircleIcon({ size = 14 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <circle cx="12" cy="12" r="9" />
        </svg>
    );
}

function CopyFromLiveButton({
    label,
    disabled,
    title,
    tip,
    onClick,
}: {
    label: string;
    disabled: boolean;
    title?: string;
    tip?: string;
    onClick: () => void;
}) {
    const button = (
        <CpmButton
            variant="outline"
            size="sm"
            disabled={disabled}
            title={title}
            leadingIcon={<MergeIcon />}
            onClick={onClick}
            style={{
                color: disabled ? undefined : 'var(--cpm-text-primary)',
                fontSize: 13,
                paddingLeft: 16,
                paddingRight: 16,
            }}
        >
            {label}
        </CpmButton>
    );
    if (!tip) return button;
    return (
        <div className="flex items-center flex-none" style={{ gap: 8 }}>
            <CpmTooltip title={label} content={tip} align="end" fit maxWidth={268}>
                <span
                    className="inline-flex items-center"
                    style={{ color: 'var(--cpm-text-tertiary)' }}
                >
                    <AlertCircleIcon />
                </span>
            </CpmTooltip>
            {button}
        </div>
    );
}
